import classNames from "classnames";
import heic2any from "heic2any";
import { noop } from "lodash";
import React, { createRef, SyntheticEvent } from "react";
import Dropzone from "react-dropzone";
import { Option } from "space-monad";
import styled from "styled-components";
import { colours } from "../../styles/colours";
import { Icon } from "../icons";
import { Flex } from "../layout";

const Wrapper = styled(Flex)`
  width: 100%;
  background: transparent;
`;

const FileName = styled(Flex)`
  text-align: center;
`;

const Cross = styled(Icon).attrs({ type: "close" })`
  margin-left: 0.4em;
  font-size: 2em;
  padding: 0.2em;
  border-radius: 100%;

  &:hover {
    background: ${colours.grey};
  }
`;

const Label = styled.label<{
  error?: boolean;
  hasValue: boolean;
  dragging?: boolean;
  disabled?: boolean;
}>`
  width: 100%;
  height: 15em;
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex-direction: column;
  padding: calc(1em - 1px) 1.2em;
  border: 1px solid;
  border-radius: 4px;
  margin-top: 8px;
  cursor: ${(props) => (props.disabled ? "no-drop" : "pointer")};
  background: ${colours.white};
  border: ${(props) =>
    props.dragging
      ? `3px dashed ${colours.grey}`
      : props.error
      ? `1px solid ${colours.red}`
      : "1px solid rgba(0, 0, 0, 0.23)"};
  ${(props) =>
    props.error
      ? `color: ${colours.red};`
      : !props.hasValue
      ? `color: #a2a3a5;`
      : ""};
`;

const HelperText = styled.div<{ error?: boolean }>`
  color: ${(props) => (props.error ? colours.red : "rgba(0, 0, 0, 0.54)")};
  color: ;
  margin: 0;
  font-size: 1em;
  margin-top: 3px;
  text-align: left;
  font-family: sans-serif;
  font-weight: 400;
  line-height: 1.66;
  margin-left: 14px;
  margin-right: 14px;
`;

type InterimFileFieldProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "multiple"
> & {
  label?: string;
  error?: boolean;
  helperText?: string;
};

export type FileFieldProps = InterimFileFieldProps &
  (
    | {
        onChange?: (value: File[] | null) => void;
        multiple: true;
        value?: File[] | null;
      }
    | {
        onChange?: (value: File | null) => void;
        value?: File | null;
        multiple?: false;
      }
  );

const AttachIcon = styled(Icon).attrs({ type: "attachment" })`
  min-width: 12px;
  font-size: 3em;
`;

const Thumbnail = styled.img`
  height: 50%;
  padding: 0.5em;
  border: 2px solid rgba(0, 0, 0, 0.1);
`;

export class FileField extends React.Component<
  FileFieldProps,
  {
    selectedFile: File | undefined;
    dirty: boolean;
    dragging: boolean;
    imagePreviewUrl: string | null;
  }
> {
  static displayName = "FileField";

  private fieldRef = createRef<HTMLInputElement>();

  constructor(props: FileFieldProps) {
    super(props);
    this.state = {
      dirty: false,
      selectedFile: undefined,
      dragging: false,
      imagePreviewUrl: null,
    };
  }

  resetField = () => {
    this.setState({ dirty: false, selectedFile: undefined });
  };

  componentDidMount = () => {
    if (this.fieldRef.current && this.props.value) {
      if (this.props.multiple) {
        if (this.props.value[0]) this.setSelectedFiled(this.props.value[0]);
      } else {
        this.setSelectedFiled(this.props.value);
      }
    }
  };

  reMount = () => {
    this.componentDidMount();
  };

  removeFile = (e: SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();

    this.setState({
      selectedFile: undefined,
      imagePreviewUrl: null,
    });

    if (this.props.onChange) this.props.onChange(null);
  };

  onDropHander = (acceptedFiles: File[]) => {
    if (this.props.onChange) {
      if (this.props.multiple) this.props.onChange(acceptedFiles);
      else this.props.onChange(acceptedFiles[0]);
    }

    this.setState({
      ...this.state,
      dirty: true,
    });
    if (acceptedFiles[0]) this.setSelectedFiled(acceptedFiles[0]);
  };

  setSelectedFiled = async (file: File) => {
    let reader = new FileReader();

    reader.onloadend = async () => {
      this.setState({
        selectedFile: file,
        imagePreviewUrl: reader.result as string,
      });
    };

    if (file.type === "image/heic") {
      const arrayBuffer = await file.arrayBuffer();
      const readerResult = await heic2any({
        blob: new Blob([arrayBuffer], { type: file.type }),
        toType: "image/jpeg",
        quality: 1,
      });

      Option(readerResult)
        .map((x) => (Array.isArray(x) ? x[0] : x))
        .fold(noop, (b) => reader.readAsDataURL(b));
    } else {
      reader.readAsDataURL(file);
    }
  };

  render() {
    const { name, label, error, helperText, disabled } = this.props;
    const { selectedFile, imagePreviewUrl } = this.state;

    return (
      <>
        <Dropzone
          onDrop={disabled ? noop : this.onDropHander}
          accept={{
            "image/png": [".png"],
            "image/jpg": [".jpg"],
            "image/jpeg": [".jpeg"],
            "image/heic": [".heic"],
            "application/pdf": [".pdf"], // OVERRIDDEN below
          }}
          multiple={false}
        >
          {({ getRootProps, getInputProps, isDragActive }) => (
            <Wrapper
              className={classNames(
                "MuiInputBase-root",
                !selectedFile && "Mui-disabled",
                this.props.className
              )}
              {...getRootProps()}
              onClick={disabled ? noop : getRootProps().onClick}
            >
              <input
                {...getInputProps()}
                accept="image/png, image/jpg, image/jpeg, image/heic, application/pdf"
                style={{ display: "none" }}
                id={`file-upload-${name || label}`}
                ref={this.fieldRef}
              />
              <Label
                disabled={disabled}
                dragging={isDragActive}
                htmlFor={`file-upload-${name || label}`}
                error={error}
                hasValue={!!selectedFile}
              >
                {imagePreviewUrl ? (
                  <Thumbnail src={imagePreviewUrl} />
                ) : (
                  <AttachIcon colour1={colours.grey} />
                )}
                <FileName>
                  {selectedFile ? selectedFile.name : label}
                  {selectedFile ? <Cross onClick={this.removeFile} /> : null}
                </FileName>
              </Label>
            </Wrapper>
          )}
        </Dropzone>
        {helperText ? (
          <HelperText error={error}>{helperText}</HelperText>
        ) : null}
      </>
    );
  }
}
